/**
 * A page from last week's build, asking for files that no longer exist.
 *
 * WHAT MATT SAW, 30 August, 10:44 PM: "The league database could not be reached … ('text/html'
 * is not a valid JavaScript MIME type.)" The database was fine. His page was from an earlier
 * deploy, and when he pressed Submit it lazily imported the Firestore chunk for the first time —
 * a filename that had been hashed out of existence by a deploy hours before. Firebase Hosting's
 * catch-all rewrite answered with `index.html`, the module loader refused to run HTML, and the
 * provider's catch reported the only thing it knew how to say. He then got a RECORDED receipt
 * from the memory store for a submission the league never received.
 *
 * HOW A PAGE GETS THAT OLD. The generated service worker calls `skipWaiting()` and
 * `clients.claim()` and its `activate` deletes every other cache, so a new worker takes over a
 * page whose JavaScript is still the previous build's — and the old chunks are gone from the
 * cache and from the origin at once. iOS does the rest on its own: a home-screen app or a
 * backgrounded tab can sit for days without ever revalidating the shell.
 *
 * THE CURE IS A RELOAD, ONCE. The shell is served network-first, so a reload lands on the current
 * build with the current chunk names. Once, because a reload that lands on a page that fails the
 * same way must not loop; the guard is per session so tomorrow's stale page can heal itself too.
 *
 * WHY NOT FIX HOSTING INSTEAD. A rewrite that 404s a missing asset would be cleaner, and Firebase
 * Hosting cannot express one: rewrites only run when no file matches, and the SPA fallback has to
 * be a catch-all. So the page has to recognise the shape of the failure itself. The service
 * worker helps by refusing to hand an HTML body to a script request — see `make-sw.mjs`.
 */

const GUARD = 'ffl:stale-reload';

/**
 * Does this error mean "the file you asked for is not the file you think it is"?
 *
 * Every engine words it differently and none of them word it as a network failure, which is the
 * distinction that matters: a real outage should fall back to the memory store and say so; a stale
 * build should reload and say nothing.
 */
export function isStaleBuildError(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name}: ${e.message}` : String(e ?? '');
  return /not a valid JavaScript MIME type/i.test(msg)            // Chromium, Firefox
    || /Failed to fetch dynamically imported module/i.test(msg)  // Chromium
    || /Importing a module script failed/i.test(msg)             // Safari
    || /error loading dynamically imported module/i.test(msg)    // Firefox
    || /Unexpected token '<'/.test(msg);                          // HTML parsed as JS, any engine
}

/**
 * Reload to the current build, if this session has not already tried.
 *
 * Returns true when a reload was started — the caller should stop rendering and wait. False means
 * it already happened once this session and the page still cannot load its own files, which is a
 * real problem to show the reader rather than hide behind another spin.
 */
export function recoverFromStaleBuild(): boolean {
  try {
    if (sessionStorage.getItem(GUARD)) return false;
    sessionStorage.setItem(GUARD, new Date().toISOString());
  } catch {
    /* Private mode with storage disabled: reload anyway, once is all we can promise. */
  }
  window.location.reload();
  return true;
}

/** Clear the guard once a build has loaded cleanly, so the next stale page can heal itself. */
export function markBuildHealthy(): void {
  try { sessionStorage.removeItem(GUARD); } catch { /* nothing to clear */ }
}
